import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PostCard, HeroCard } from "../components/PostCard";
import NewsletterForm from "../components/NewsletterForm";
import SEO from "../components/SEO";
import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";

const MARQUEE_ITEMS = [
  { label: "NEWS", to: "/news" },
  { label: "ATP", to: "/category/ATP" },
  { label: "WTA", to: "/category/WTA" },
  { label: "PODCAST", to: "/podcasts" },
];

export default function Home() {
  const [hero, setHero] = useState({ article: null, podcast: null, digital: null });
  const [articles, setArticles] = useState([]);
  const [podcasts, setPodcasts] = useState([]);
  const [digital, setDigital] = useState([]);

  useEffect(() => {
    api.get("/posts/featured-mix").then((r) => setHero(r.data));
    api.get("/posts", { params: { type: "article", limit: 6 } }).then((r) => setArticles(r.data));
    api.get("/posts", { params: { type: "podcast", limit: 4 } }).then((r) => setPodcasts(r.data));
    api.get("/posts", { params: { type: "digital", limit: 4 } }).then((r) => setDigital(r.data));
  }, []);

  // Hero composition — featured article on the left (big), featured podcast + digital stacked on the right.
  // If a featured slot is empty (no posts of that type yet), fall back to the most recent article.
  const heroLeft = hero.article || articles[0] || null;
  const heroRightTop = hero.podcast || podcasts[0] || null;
  const heroRightBottom = hero.digital || digital[0] || null;

  // Skip whatever is shown in the hero from the "More Stories" feed below.
  const heroSlugs = new Set([heroLeft, heroRightTop, heroRightBottom].filter(Boolean).map((p) => p.slug));
  const moreArticles = articles.filter((a) => !heroSlugs.has(a.slug)).slice(0, 4);

  return (
    <div data-testid="home-page" className="bg-[#050505]">
      <SEO />
      {/* Static category bar — clickable links, no animation */}
      <div className="border-b border-white/10 bg-black py-4 sm:py-6">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center justify-center gap-x-5 gap-y-3 px-4 sm:gap-x-8 sm:px-6 lg:gap-x-20 lg:px-12">
          {MARQUEE_ITEMS.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              data-testid={`marquee-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className="font-display text-xl font-bold uppercase tracking-tighter text-white transition-colors hover:text-[#D4FF00] sm:text-2xl md:text-3xl lg:text-4xl"
            >
              {item.label} <span className="text-[#D4FF00]">/</span>
            </Link>
          ))}
        </div>
      </div>

      {/* Hero bento */}
      <section className="mx-auto max-w-[1400px] px-4 py-10 sm:px-6 sm:py-12 lg:px-12 lg:py-16">
        <div className="mb-6 flex items-end justify-between sm:mb-8">
          <div className="reveal-up">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D4FF00] sm:text-xs">The Cover</div>
            <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-white sm:text-3xl md:text-4xl">This Week in Tennis</h2>
          </div>
          <Link to="/articles" className="hidden items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-white/70 hover:text-[#D4FF00] md:flex">
            All Articles <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid gap-6 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-8">{heroLeft ? <HeroCard post={heroLeft} /> : <div className="aspect-[16/9] w-full bg-[#0a0a0a]" />}</div>
          <div className="flex flex-col gap-6 lg:col-span-4 lg:gap-8">
            {heroRightTop && <PostCard post={heroRightTop} />}
            {heroRightBottom && <PostCard post={heroRightBottom} />}
          </div>
        </div>
      </section>

      {/* Podcasts section */}
      <section className="border-y border-white/10 bg-[#0a0a0a]">
        <div className="mx-auto max-w-[1400px] px-4 py-12 sm:px-6 sm:py-16 lg:px-12 lg:py-24">
          <div className="mb-8 flex items-end justify-between gap-8 sm:mb-10 sm:gap-16 lg:gap-24">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#E36A53] sm:text-xs">The Baseline Breakdown Podcast</div>
              <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-white sm:text-3xl md:text-5xl">Recent Episodes</h2>
            </div>
            <Link to="/podcasts" data-testid="link-all-podcasts" className="flex shrink-0 items-center gap-2 pb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white/70 hover:text-[#E36A53] sm:text-xs">
              All <span className="hidden sm:inline">Episodes</span> <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
            {podcasts.map((p) => <PostCard key={p.id} post={p} />)}
          </div>
        </div>
      </section>

      {/* Digital section */}
      <section className="mx-auto max-w-[1400px] px-4 py-12 sm:px-6 sm:py-16 lg:px-12 lg:py-24">
        <div className="mb-8 flex items-end justify-between sm:mb-10">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4FB7D7] sm:text-xs">The Screen</div>
            <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-white sm:text-3xl md:text-5xl">Digital Content</h2>
          </div>
          <Link to="/digital" data-testid="link-all-digital" className="flex shrink-0 items-center gap-2 pb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white/70 hover:text-[#4FB7D7] sm:text-xs">
            Watch All <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
          {digital.map((p) => <PostCard key={p.id} post={p} />)}
        </div>
      </section>

      {/* More articles */}
      {moreArticles.length > 0 && (
        <section className="border-t border-white/10 bg-[#0a0a0a]">
          <div className="mx-auto max-w-[1400px] px-4 py-12 sm:px-6 sm:py-16 lg:px-12 lg:py-24">
            <div className="mb-8 sm:mb-10">
              <h2 className="font-display text-2xl font-semibold tracking-tight text-white sm:text-3xl md:text-5xl">More Stories</h2>
            </div>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
              {moreArticles.map((p) => <PostCard key={p.id} post={p} />)}
            </div>
          </div>
        </section>
      )}

      <NewsletterForm variant="stripe" />
    </div>
  );
}
